import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { assemblyPctNum, journeyOf, mesOverdue, parseOvDate, type AssemblyOrderStatus, type OverviewConfirmation, type OverviewVessel } from '@technohull/shared';
import { useCellHistory, useOverview, useOverviewWrite, useSetOverviewConfirm } from '../features/hooks.js';
import { useNameResolver } from '../features/directory.js';
import { useAuth } from '../auth/AuthContext.js';
import { usePrefs } from '../settings/SettingsContext.js';
import { useSeason } from '../season/SeasonContext.js';
import { Chip, EmptyState, ErrorState, FilterBar, FilterSelect, InlineError, InlineLoading, LoadingState, LogoSpin, Modal, PageHeader, Pill, SearchInput, Segmented, SyncLabel } from '../components/ui.js';
import { JourneyLegend, JourneyStrip, journeySummary } from '../components/JourneyStrip.js';
import { IconAlertTriangle, IconCheck, IconClipboard, IconPause, IconRefresh } from '../components/icons.js';
import { useDateFormatter, useFormatDate } from '../lib/format.js';
import { MQ, useIsPhone, useMediaQuery } from '../lib/media.js';
import { fold, hasText } from '../lib/search.js';
import { sortRows, useTableSort } from '../lib/tableSort.js';
import { useTabState } from '../lib/tabState.js';
import { SortMark } from '../components/SortMark.js';

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 86_400_000;

// The status columns, in the production sheet's order. `label` is the sheet's own short name (the history
// card and the vessel sheet show it); `title` is the full name where it is unambiguous; `head` is how the
// grid header writes it, one entry per line (a soft hyphen lets a long word break).
type StatusField = { key: keyof OverviewVessel; label: string; title?: string; head: string[] };
const STATUS_FIELDS: StatusField[] = [
  { key: 'engineInstall', label: 'Eng Inst', title: 'Engine Install', head: ['Eng.', 'install'] },
  { key: 'engineStartup', label: 'Eng Start', title: 'Engine Start-up', head: ['Eng.', 'start‑up'] },
  { key: 'seaTrial', label: 'Sea Trial', title: 'Sea Trial', head: ['Sea', 'trial'] },
  { key: 'pdi', label: 'PDI', title: 'Pre-Delivery Inspection', head: ['PDI'] },
  { key: 'uh', label: 'UH', title: 'Upholstery', head: ['Uphol­stery'] },
  { key: 'stickers', label: 'STK', title: 'Stickers', head: ['Stickers'] },
  { key: 'wa', label: 'WA', title: 'Washing', head: ['Washing'] },
  { key: 'pol', label: 'POL', title: 'Polishing', head: ['Polish­ing'] },
  { key: 'fw', label: 'FW', head: ['FW'] },
  { key: 'frl', label: 'FRL', head: ['FRL'] },
  { key: 'anf', label: 'AnF', head: ['AnF'] },
];
const fieldName = (f: StatusField) => f.title ?? f.label;

/** a status value in the one status vocabulary: Yes is ok (green), Hold is warn (amber, with the pause
 *  mark), No is neutral. Anything else (unset, free text) stays unmarked. Case/space-insensitive. */
type StatusKind = 'ok' | 'hold' | 'no' | '';
function statusKind(v: string | null | undefined): StatusKind {
  switch (String(v ?? '').trim().toLowerCase()) {
    case 'yes': return 'ok';
    case 'hold': return 'hold';
    case 'no': return 'no';
    default: return '';
  }
}
/** the `.pill` class for a status value (a hold is the warn pill; No stays the neutral pill). */
const pillKind = (v: string | null | undefined) => { const k = statusKind(v); return k === 'no' ? '' : k; };

// the vessel sheet's status choices, in the order of the grid's select ('' = not set)
const STATUS_OPTIONS = [
  { value: '', label: '–', title: 'Not set' },
  { value: 'Yes', label: 'Yes' },
  { value: 'Hold', label: 'Hold' },
  { value: 'No', label: 'No' },
];
const hasOrder = (s?: AssemblyOrderStatus) => !!s && (!!s.printedAt || s.leaderChecked || !!s.comments);
const hasHold = (v: OverviewVessel) => STATUS_FIELDS.some((f) => statusKind(String(v[f.key] ?? '')) === 'hold');
const isOverdue = (v: OverviewVessel, now: Date) => !v.left && mesOverdue(v, now);
// accents and case don't matter: "νικος" finds Νίκος
const matchesQuery = (v: OverviewVessel, query: string) => hasText(fold(query.trim()), v.name, v.sap, v.model);
// the grid's sortable columns: the vessel name or SAP, delivery, assembly, each status, the sign-offs and Left
type OvSortKey = 'name' | 'sap' | 'delivery' | 'assembly' | 'sales' | 'logistics' | 'left' | Extract<StatusField['key'], string>;
type DeliveryFilter = 'active' | 'all' | 'delivered';
const inDelivery = (v: OverviewVessel, f: DeliveryFilter) => (f === 'active' ? !v.left : f === 'delivered' ? v.left : true);

export function Overview() {
  const { data, isLoading, isFetching, error, refetch } = useOverview();
  const { can } = useAuth();
  const { activeSeason } = useSeason();
  const nameOf = useNameResolver();
  const write = useOverviewWrite();
  const confirm = useSetOverviewConfirm();
  const canWrite = can('overview:write');
  const fmtDate = useFormatDate();
  const fmtDelivery = useDeliveryFormat();
  // a hover-capable pointer keeps the hover history card and the order popover; touch (and any phone)
  // gets the tap path instead: the vessel cell opens a sheet with every status, its history and sign-offs
  const canHover = useMediaQuery(MQ.hover);
  const isPhone = useIsPhone();
  const sheetTap = isPhone || !canHover;
  const { cellHistory: cellHistoryOn } = usePrefs(); // Settings › Display › Cell history
  const [sheetRow, setSheetRow] = useState<number | null>(null);

  // ?q= seeds the search: a boat picked in the top-bar search lands here as /overview?q=<SAP>, also while
  // the Overview is already open. Typing writes it back (replace), so a reload or a shared link keeps it.
  const [params, setParams] = useSearchParams();
  const urlQ = params.get('q') ?? '';
  const [q, setQState] = useState(urlQ);
  const seenQ = useRef(urlQ);
  // a ?q= that arrived from outside, until the data is in to check it against the Show filter
  const [arrivedQ, setArrivedQ] = useState<string | null>(urlQ || null);

  // the filters stay as you left them when you come back to the page (this tab only)
  const [deliveryFilter, setDeliveryFilter] = useTabState<DeliveryFilter>('overview.show', 'active');
  const [modelSel, setModelSel] = useTabState('overview.model', '');      // '' = all models
  const [statusCol, setStatusCol] = useTabState('overview.statusCol', ''); // '' = any status column
  const [statusVal, setStatusVal] = useTabState('overview.status', '');    // '' = any value | Yes | No | Hold
  const [overdueOnly, setOverdueOnly] = useTabState('overview.overdue', false);
  // a header click sorts by that column: ascending, descending, then back to the sheet's order
  const { sort, dirOf, toggle: toggleSort, th: sortTh } = useTableSort<OvSortKey>('overview');
  // the status cell the pointer is currently over (drives the change-history popover).
  const [hist, setHist] = useState<{ rowId: number; columnId: number; label: string; vessel: string; top: number; left: number } | null>(null);
  // the vessel whose order-status badge is hovered (drives the Assembly-order popover).
  const [ordPop, setOrdPop] = useState<{ sap: string; vessel: string; top: number; left: number } | null>(null);

  const setQ = (v: string) => {
    setQState(v);
    seenQ.current = v;
    setParams((p) => {
      const n = new URLSearchParams(p);
      if (v) n.set('q', v);
      else n.delete('q');
      return n;
    }, { replace: true });
  };
  useEffect(() => {
    if (urlQ === seenQ.current) return; // our own write-back
    seenQ.current = urlQ;
    setQState(urlQ);
    if (!urlQ) return;
    // the picked boat must not be hidden by a filter left on from before
    setModelSel(''); setStatusVal(''); setStatusCol(''); setOverdueOnly(false);
    setArrivedQ(urlQ);
  }, [urlQ]);
  // a boat that has already left sits outside "In build": widen Show so the picked boat is on screen
  useEffect(() => {
    if (!arrivedQ || !data) return;
    setArrivedQ(null);
    const hits = data.vessels.filter((v) => matchesQuery(v, arrivedQ));
    if (hits.length && !hits.some((v) => inDelivery(v, deliveryFilter))) setDeliveryFilter('all');
  }, [arrivedQ, data, deliveryFilter]);


  // the Assembly-order card opens on click (not hover) so it works on touch + keyboard; dismiss it on
  // Escape or an outside click, like the page-notes popover.
  useEffect(() => {
    if (!ordPop) return;
    const onDoc = (e: globalThis.MouseEvent) => {
      const t = e.target as HTMLElement;
      if (!t.closest('[data-ordpop]') && !t.closest('[data-ordpop-trigger]')) setOrdPop(null);
    };
    const onKey = (e: globalThis.KeyboardEvent) => { if (e.key === 'Escape') setOrdPop(null); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [ordPop]);

  // one clock per data load, so the overdue chip, the delivery cells and the filter agree.
  const now = useMemo(() => new Date(), [data]);
  // distinct models present, for the model filter.
  const models = useMemo(() => [...new Set((data?.vessels ?? []).map((v) => v.model).filter(Boolean))].sort(), [data]);

  // the boats in view before the status filters: Show + Model. The Hold / Overdue chips count from here.
  const scoped = useMemo(() => {
    let vessels = (data?.vessels ?? []).filter((v) => inDelivery(v, deliveryFilter));
    if (modelSel) vessels = vessels.filter((v) => v.model === modelSel);
    return vessels;
  }, [data, deliveryFilter, modelSel]);
  const holdCount = useMemo(() => scoped.filter(hasHold).length, [scoped]);
  const overdueCount = useMemo(() => scoped.filter((v) => isOverdue(v, now)).length, [scoped, now]);

  const rows = useMemo(() => {
    let vessels = scoped;
    // status: a Yes/No/Hold value, in a chosen column or ANY status column.
    // (case and spaces in the sheet's values don't matter: "HOLD " is a hold)
    if (statusVal) {
      const want = statusKind(statusVal);
      const is = (v: OverviewVessel, key: keyof OverviewVessel) => statusKind(String(v[key] ?? '')) === want;
      vessels = statusCol
        ? vessels.filter((v) => is(v, statusCol as keyof OverviewVessel))
        : vessels.filter((v) => STATUS_FIELDS.some((f) => is(v, f.key)));
    }
    if (overdueOnly) vessels = vessels.filter((v) => isOverdue(v, now));
    if (q.trim()) vessels = vessels.filter((v) => matchesQuery(v, q));
    const conf = data?.confirmations ?? {};
    return sortRows(vessels, sort, (v, key): unknown => {
      switch (key) {
        case 'name': return v.name;
        case 'sap': return v.sap;
        case 'delivery': return parseOvDate(v.deliveryDate)?.getTime() ?? v.deliveryDate;
        case 'assembly': return assemblyPctNum(v);
        case 'sales': return !!conf[v.sap]?.confirmedSales;
        case 'logistics': return !!conf[v.sap]?.confirmedLogistics;
        case 'left': return v.left;
        default: return v[key];
      }
    });
  }, [scoped, q, statusCol, statusVal, overdueOnly, now, sort, data]);

  const activeFilters = (q.trim() ? 1 : 0) + (deliveryFilter !== 'active' ? 1 : 0) + (modelSel ? 1 : 0) + (statusVal ? 1 : 0) + (overdueOnly ? 1 : 0);
  const clearFilters = () => { setQ(''); setDeliveryFilter('active'); setModelSel(''); setStatusVal(''); setStatusCol(''); setOverdueOnly(false); };

  const cols = data?.cols ?? {};
  const sheetId = data?.sheetId ?? '';
  const confirmations = data?.confirmations ?? {};
  const orderStatus = data?.orderStatus ?? {};
  const sheetVessel = sheetRow == null ? undefined : (data?.vessels ?? []).find((v) => v.rowId === sheetRow);

  const setStatus = (v: OverviewVessel, field: keyof OverviewVessel, value: string) => {
    const columnId = cols[field as string];
    if (!columnId) return;
    write.mutate({ sheetId, rowId: v.rowId, cells: [{ columnId, value: value || null }] });
  };

  const setDelivery = (v: OverviewVessel, value: string) => {
    const columnId = cols['deliveryDate'];
    if (!columnId) return;
    write.mutate({ sheetId, rowId: v.rowId, cells: [{ columnId, value: value || null }] });
  };

  const setConfirm = (v: OverviewVessel, field: 'sales' | 'logistics', value: boolean) => {
    if (!v.sap) return;
    confirm.mutate({ sap: v.sap, field, value });
  };

  // "Left" — the Smartsheet checkbox marking a vessel as departed/delivered.
  const setLeft = (v: OverviewVessel, checked: boolean) => {
    const columnId = cols['left'];
    if (!columnId) return;
    write.mutate({ sheetId, rowId: v.rowId, cells: [{ columnId, value: checked }] });
  };

  const scopeWord = deliveryFilter === 'active' ? ' in build' : deliveryFilter === 'delivered' ? ' delivered' : '';
  const meta = (
    <>
      <span title="Vessels shown">{rows.length} vessel{rows.length === 1 ? '' : 's'}{scopeWord}</span>
      {activeSeason ? ` · season ${activeSeason}` : ''} · mirrors the production sheet
    </>
  );

  return (
    <>
      <PageHeader title="Overview" meta={meta}>
        <button className="btn btn-sm" disabled={isFetching} onClick={() => void refetch()}>
          <SyncLabel busy={isFetching} icon={<IconRefresh size={15} />} />
        </button>
      </PageHeader>

      {data && (
        <FilterBar search={<SearchInput value={q} onChange={setQ} placeholder={isPhone ? 'Search name or SAP' : 'Search name, SAP or model'} width={250} />} activeCount={activeFilters} onClear={clearFilters}>
          <Chip active={statusVal === 'Hold'} onClick={() => setStatusVal(statusVal === 'Hold' ? '' : 'Hold')} title="Boats with a status on hold" ariaLabel={`On hold, ${holdCount}`}>
            <IconPause size={13} className="ovg-chip-ic" />Hold <b className="ovg-chip-n">{holdCount}</b>
          </Chip>
          <Chip active={overdueOnly} onClick={() => setOverdueOnly((o) => !o)} title="Delivery date passed, not yet delivered" ariaLabel={`Overdue, ${overdueCount}`}>
            <IconAlertTriangle size={13} className="ovg-chip-ic" />Overdue <b className="ovg-chip-n">{overdueCount}</b>
          </Chip>
          <FilterSelect
            label="Show"
            value={deliveryFilter}
            onChange={(v) => setDeliveryFilter(v as DeliveryFilter)}
            options={[{ value: 'active', label: 'In build' }, { value: 'all', label: 'All boats' }, { value: 'delivered', label: 'Delivered' }]}
          />
          {models.length > 1 && (
            <FilterSelect
              label="Model"
              value={modelSel}
              onChange={setModelSel}
              options={[{ value: '', label: 'All models' }, ...models.map((m) => ({ value: m, label: m }))]}
            />
          )}
          <FilterSelect
            label="Status"
            value={statusVal}
            onChange={(v) => { setStatusVal(v); if (!v) setStatusCol(''); }}
            options={[{ value: '', label: 'Any' }, { value: 'Yes', label: 'Yes' }, { value: 'Hold', label: 'Hold' }, { value: 'No', label: 'No' }]}
          />
          {statusVal && (
            <FilterSelect
              label="in"
              value={statusCol}
              onChange={setStatusCol}
              ariaLabel="Limit the status filter to one column"
              options={[{ value: '', label: 'any column' }, ...STATUS_FIELDS.map((f) => ({ value: f.key as string, label: fieldName(f) }))]}
            />
          )}
          <JourneyLegend className="ovg-key" />
        </FilterBar>
      )}

      {write.isError && <InlineError error={write.error} onDismiss={() => write.reset()} />}
      {confirm.isError && <InlineError error={confirm.error} onDismiss={() => confirm.reset()} />}
      {isLoading && <LoadingState label="Loading overview…" />}
      {error && <ErrorState error={error} onRetry={() => void refetch()} />}

      {data && (
        <div className="tbl-wrap is-grid ovg-wrap grid-fill">
          <table className="tbl ovg">
            <thead>
              <tr>
                <th className="pin-col ovg-v" aria-sort={sort && (sort.key === 'name' || sort.key === 'sap') ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}>
                  <span className="ovg-vh">
                    <span className="ovg-vh-sort">
                      <button type="button" className="ov-sort" onClick={() => toggleSort('name')} title="Sort by vessel name">Vessel<SortMark dir={dirOf('name')} /></button>
                      <span aria-hidden="true">·</span>
                      <button type="button" className="ov-sort" onClick={() => toggleSort('sap')} title="Sort by SAP">SAP<SortMark dir={dirOf('sap')} /></button>
                    </span>
                    <span className="ovg-vh-j">Build journey</span>
                  </span>
                </th>
                <th {...sortTh('delivery', 'ovg-dl')}>Delivery<SortMark dir={dirOf('delivery')} /></th>
                <th {...sortTh('assembly', 'ovg-as')}>Assembly<SortMark dir={dirOf('assembly')} /></th>
                {STATUS_FIELDS.map((f) => {
                  const key = f.key as Extract<StatusField['key'], string>;
                  return (
                    <th key={key} {...sortTh(key, 'ovg-s')} title={`${fieldName(f)}: click to sort`}>
                      {f.head.map((line, i) => <span key={i}>{i > 0 && <br />}{line}</span>)}
                      <SortMark dir={dirOf(key)} />
                    </th>
                  );
                })}
                <th {...sortTh('sales', 'ovg-c')} title="Confirmed by Sales: click to sort">Sales<IconCheck size={12} className="ovg-th-ic" /><SortMark dir={dirOf('sales')} /></th>
                <th {...sortTh('logistics', 'ovg-c')} title="Confirmed by Logistics: click to sort">Logistics<IconCheck size={12} className="ovg-th-ic" /><SortMark dir={dirOf('logistics')} /></th>
                <th {...sortTh('left', 'ovg-c')} title="Vessel has left / departed: click to sort">Left<SortMark dir={dirOf('left')} /></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((v) => {
                const conf = confirmations[v.sap];
                const pct = Math.round(assemblyPctNum(v));
                const ordered = hasOrder(orderStatus[v.sap]);
                return (
                <tr key={v.rowId}>
                  <td className="pin-col ovg-v">
                    {sheetTap ? (
                      <button type="button" className="ov-vopen" aria-haspopup="dialog" onClick={() => setSheetRow(v.rowId)}>
                        <span className="ovg-vn-row">
                          <span className="ovg-vn">{v.name}</span>
                          {ordered && <><IconClipboard size={13} className="ovg-ord-ic" /><span className="sr-only">, has an assembly order</span></>}
                        </span>
                        <span className="ovg-vm">
                          <span className="ovg-vid">{v.sap} · {v.model}</span>
                          <JourneyStrip vessel={v} size="sm" />
                        </span>
                      </button>
                    ) : (<>
                      <span className="ovg-vn-row">
                        <span className="ovg-vn">{v.name}</span>
                        {ordered && (
                          <button
                            type="button"
                            className="ov-ordbtn"
                            data-ordpop-trigger
                            aria-label={`Assembly order status - ${v.name}`}
                            aria-expanded={ordPop?.sap === v.sap}
                            title="Assembly order"
                            onClick={(e) => {
                              if (ordPop?.sap === v.sap) { setOrdPop(null); return; }
                              const r = e.currentTarget.getBoundingClientRect();
                              setOrdPop({
                                sap: v.sap,
                                vessel: v.name,
                                top: Math.min(r.bottom + 4, Math.max(8, window.innerHeight - 240)),
                                left: Math.min(r.left, window.innerWidth - 300),
                              });
                            }}
                          ><IconClipboard size={13} /></button>
                        )}
                      </span>
                      <span className="ovg-vm">
                        <span className="ovg-vid">{v.sap} · {v.model}</span>
                        <JourneyStrip vessel={v} size="sm" />
                      </span>
                    </>)}
                  </td>
                  <td className="ovg-dl">
                    <DeliveryCell
                      v={v}
                      now={now}
                      fmt={fmtDelivery}
                      editable={canWrite}
                      onChange={(value) => setDelivery(v, value)}
                    />
                  </td>
                  <td className="ovg-as"><AssemblyMeter pct={pct} /></td>
                  {STATUS_FIELDS.map((f) => {
                    const value = String(v[f.key] ?? '');
                    const columnId = cols[f.key as string];
                    // the hover card needs a pointer that hovers (and isn't turned off in Settings); touch reaches
                    // the same history in the vessel sheet
                    const hoverHist = !!columnId && canHover && cellHistoryOn;
                    return (
                      <td
                        key={f.key as string}
                        className={`ovg-s${hoverHist ? ' has-hist' : ''}`}
                        onMouseEnter={
                          hoverHist
                            ? (e) => {
                                const r = e.currentTarget.getBoundingClientRect();
                                setHist({ rowId: v.rowId, columnId, label: fieldName(f), vessel: v.name, top: Math.min(r.bottom + 4, Math.max(8, window.innerHeight - 310)), left: Math.min(r.left, window.innerWidth - 280) });
                              }
                            : undefined
                        }
                        onMouseLeave={hoverHist ? () => setHist(null) : undefined}
                      >
                        {canWrite && columnId ? (
                          <StatusSelect value={value} onChange={(next) => setStatus(v, f.key, next)} label={`${fieldName(f)} - ${v.name}`} />
                        ) : (
                          <StatusMark value={value} />
                        )}
                      </td>
                    );
                  })}
                  {(['sales', 'logistics'] as const).map((field) => {
                    const on = field === 'sales' ? !!conf?.confirmedSales : !!conf?.confirmedLogistics;
                    const by = field === 'sales' ? conf?.salesBy : conf?.logisticsBy;
                    const at = field === 'sales' ? conf?.salesAt : conf?.logisticsAt;
                    return (
                      <td key={field} className="ovg-c">
                        <label className="ov-cbl">
                          <input
                            type="checkbox"
                            checked={on}
                            disabled={!canWrite || !v.sap}
                            onChange={(e) => setConfirm(v, field, e.target.checked)}
                            aria-label={`${field === 'sales' ? 'Sales' : 'Logistics'} confirmed - ${v.name}`}
                            title={on && by ? `${nameOf(by)}${at ? ` · ${fmtDate(at)}` : ''}` : ''}
                          />
                        </label>
                      </td>
                    );
                  })}
                  <td className="ovg-c">
                    {canWrite && cols['left'] ? (
                      <label className="ov-cbl">
                        <input type="checkbox" checked={v.left} onChange={(e) => setLeft(v, e.target.checked)} aria-label={`Left / departed - ${v.name}`} title="Vessel has left / departed" />
                      </label>
                    ) : v.left ? (
                      <span className="ov-yn ok" title="Vessel has left / departed"><IconCheck size={11} />Left</span>
                    ) : (
                      <span className="ov-yn">–</span>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {/* under the grid, not centred in a row of a table wider than a phone */}
      {data && rows.length === 0 && (
        <div className="grid-empty">
          <EmptyState title="No vessels match" hint={activeFilters > 0 ? 'Try clearing the filters above.' : 'No vessels in this view.'} />
          {activeFilters > 0 && <button type="button" className="btn btn-sm" onClick={clearFilters}>Clear filters</button>}
        </div>
      )}

      {hist && <CellHistoryPopover {...hist} />}
      {ordPop && <OrderStatusPopover status={orderStatus[ordPop.sap]} vessel={ordPop.vessel} top={ordPop.top} left={ordPop.left} />}
      {sheetVessel && (
        <VesselSheet
          v={sheetVessel}
          conf={confirmations[sheetVessel.sap]}
          order={orderStatus[sheetVessel.sap]}
          cols={cols}
          canWrite={canWrite}
          onStatus={(field, value) => setStatus(sheetVessel, field, value)}
          onDelivery={(value) => setDelivery(sheetVessel, value)}
          onConfirm={(field, value) => setConfirm(sheetVessel, field, value)}
          onLeft={(checked) => setLeft(sheetVessel, checked)}
          onClose={() => setSheetRow(null)}
        />
      )}
    </>
  );
}

/** a read-only status in the one vocabulary: Yes green, Hold amber with the pause mark, No neutral. */
function StatusMark({ value }: { value: string }) {
  const k = statusKind(value);
  return (
    <span className={`ov-yn ${k}`}>
      {k === 'hold' && <IconPause size={10} />}
      {value || '–'}
    </span>
  );
}

/** an editable status: the native select, dressed as the status mark it holds. */
function StatusSelect({ value, onChange, label }: { value: string; onChange: (v: string) => void; label: string }) {
  const k = statusKind(value);
  return (
    <span className={`ov-st ${k}`}>
      {k === 'hold' && <IconPause size={10} className="ov-st-ic" />}
      <select className={`stat-sel ${k}`} value={value} onChange={(e) => onChange(e.target.value)} aria-label={label}>
        <option value="">–</option>
        <option>Yes</option>
        <option>Hold</option>
        <option>No</option>
      </select>
    </span>
  );
}

/** the Assembly % meter: a thin accent bar (green when complete) and the figure beside it. */
function AssemblyMeter({ pct }: { pct: number }) {
  const w = Math.max(0, Math.min(100, pct));
  return (
    <span className="ovg-as-in">
      <span className={`ovg-mt${w >= 100 ? ' full' : ''}`} aria-hidden="true"><i style={{ width: `${w}%` }} /></span>
      <b>{pct}%</b>
    </span>
  );
}

type DeliveryFmt = (iso: string, now: Date) => string;
/** a delivery date in the user's format with its weekday ("Sun 11/10"), the year only when it isn't this
 *  one. */
function useDeliveryFormat(): DeliveryFmt {
  const df = useDateFormatter();
  return useCallback((iso, now) => df.date(iso.slice(0, 10), { weekday: 'short', year: 'auto', now }) || iso, [df]);
}

/** where a delivery stands, in words and tone: confirmed / left (ok), late (err), else how far off. */
function deliveryRel(v: OverviewVessel, now: Date): { tone: 'ok' | 'late' | ''; label: string } | null {
  if (v.left) return { tone: 'ok', label: 'Left' };
  if (v.deliveryConfirmed) return { tone: 'ok', label: 'Confirmed' };
  const d = parseOvDate(v.deliveryDate);
  if (!d) return null;
  const days = Math.round((+d - Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())) / DAY_MS);
  if (mesOverdue(v, now)) return { tone: 'late', label: days >= 0 ? 'Due today' : `${-days} day${days === -1 ? '' : 's'} late` };
  if (days === 0) return { tone: '', label: 'today' };
  if (days === 1) return { tone: '', label: 'tomorrow' };
  return { tone: '', label: `in ${days} days` };
}

/** the delivery date and where it stands. With write access the date is the native date field, laid over
 *  the text: it shows on hover or focus, and a tap anywhere on the date opens the picker. */
function DeliveryCell({ v, now, fmt, editable, onChange }: {
  v: OverviewVessel;
  now: Date;
  fmt: DeliveryFmt;
  editable: boolean;
  onChange: (value: string) => void;
}) {
  const iso = ISO_DATE.test(v.deliveryDate);
  const text = iso ? fmt(v.deliveryDate, now) : v.deliveryDate || '–';
  const rel = deliveryRel(v, now);
  return (
    <>
      <span className={`ovg-dl-date${editable ? ' is-edit' : ''}`}>
        <span className="ovg-dl-txt" aria-hidden={editable || undefined}>{text}</span>
        {editable && (
          <input
            className="ovg-dl-in"
            type="date"
            value={iso ? v.deliveryDate : ''}
            onChange={(e) => onChange(e.target.value)}
            aria-label={`Delivery date - ${v.name}`}
            title={!iso && v.deliveryDate ? `Current: ${v.deliveryDate}` : ''}
          />
        )}
      </span>
      {rel && (
        <small className={`ovg-dl-rel${rel.tone ? ` ${rel.tone}` : ''}`}>
          {rel.tone === 'late' && <IconAlertTriangle size={12} />}
          {rel.tone === 'ok' && <IconCheck size={12} />}
          {rel.label}
        </small>
      )}
    </>
  );
}

/** the touch way into a vessel's row (Overview on phones and touch screens): every status column by its
 *  full name with a Yes / Hold / No segmented and its latest change, the delivery date, the Sales and
 *  Logistics sign-offs with who and when as text, Left, and the Assembly order. Edits save as in the grid. */
function VesselSheet({ v, conf, order, cols, canWrite, onStatus, onDelivery, onConfirm, onLeft, onClose }: {
  v: OverviewVessel;
  conf?: OverviewConfirmation;
  order?: AssemblyOrderStatus;
  cols: Record<string, number>;
  canWrite: boolean;
  onStatus: (field: keyof OverviewVessel, value: string) => void;
  onDelivery: (value: string) => void;
  onConfirm: (field: 'sales' | 'logistics', value: boolean) => void;
  onLeft: (checked: boolean) => void;
  onClose: () => void;
}) {
  const nameOf = useNameResolver();
  const fmtDate = useFormatDate();
  const fmtDelivery = useDeliveryFormat();
  const now = useMemo(() => new Date(), []);
  const steps = useMemo(() => journeyOf(v, now), [v, now]);
  const pct = Math.round(assemblyPctNum(v));
  const delIso = ISO_DATE.test(v.deliveryDate);
  const rel = deliveryRel(v, now);
  return (
    <Modal sheet dataTitle title={v.name} onClose={onClose} closeLabel="Close vessel">
      <div className="ovs">
        <div className="ovs-sub">{v.sap} · {v.model}</div>
        {/* the strip at full width, then where it stands in words: seven labels don't fit a phone */}
        <div className="ovs-journey">
          <JourneyStrip steps={steps} size="lg" />
          <p className="ovs-jtxt">{journeySummary(steps).replace(/^Build journey: /, '')}</p>
        </div>
        <div className="ovs-facts">
          <div className="ovs-sec">
            <span className="field-label">Assembly</span>
            <AssemblyMeter pct={pct} />
          </div>
          <div className="ovs-sec">
            <label className="field-label" htmlFor="ovs-delivery">Delivery</label>
            {canWrite && cols['deliveryDate'] ? (
              <input
                id="ovs-delivery"
                className="field"
                type="date"
                value={delIso ? v.deliveryDate : ''}
                onChange={(e) => onDelivery(e.target.value)}
                title={!delIso && v.deliveryDate ? `Current: ${v.deliveryDate}` : ''}
              />
            ) : (
              <span id="ovs-delivery" className="ovs-del">{delIso ? fmtDelivery(v.deliveryDate, now) : v.deliveryDate || '–'}</span>
            )}
            {rel && (
              <small className={`ovg-dl-rel${rel.tone ? ` ${rel.tone}` : ''}`}>
                {rel.tone === 'late' && <IconAlertTriangle size={12} />}
                {rel.tone === 'ok' && <IconCheck size={12} />}
                {rel.label}
              </small>
            )}
          </div>
        </div>
        <div className="ovs-checks">
          {(['sales', 'logistics'] as const).map((field) => {
            const on = field === 'sales' ? !!conf?.confirmedSales : !!conf?.confirmedLogistics;
            const by = field === 'sales' ? conf?.salesBy : conf?.logisticsBy;
            const at = field === 'sales' ? conf?.salesAt : conf?.logisticsAt;
            return (
              <div key={field} className="ovs-check">
                <label className="check-row">
                  <input type="checkbox" checked={on} disabled={!canWrite || !v.sap} onChange={(e) => onConfirm(field, e.target.checked)} />
                  {field === 'sales' ? 'Confirmed by Sales' : 'Confirmed by Logistics'}
                </label>
                {on && by && <span className="ovs-who">{nameOf(by)}{at ? ` · ${fmtDate(at)}` : ''}</span>}
              </div>
            );
          })}
          <label className="check-row">
            <input type="checkbox" checked={v.left} disabled={!canWrite || !cols['left']} onChange={(e) => onLeft(e.target.checked)} />
            Left / departed
          </label>
        </div>
        <h4 className="ovs-hd">Status</h4>
        <ul className="ovs-stats">
          {STATUS_FIELDS.map((f) => (
            <StatusRow key={f.key as string} v={v} field={f} columnId={cols[f.key as string]} canWrite={canWrite} onChange={(value) => onStatus(f.key, value)} />
          ))}
        </ul>
        {hasOrder(order) && order && (
          <div className="ovs-order">
            <h4 className="ovs-hd">Assembly order</h4>
            <OrderStatusBody status={order} />
          </div>
        )}
      </div>
    </Modal>
  );
}

/** one status in the vessel sheet: its full name, the 44px segmented (or the status mark when read-only)
 *  and the latest recorded change from the same Smartsheet history the desktop hover card shows. */
function StatusRow({ v, field, columnId, canWrite, onChange }: {
  v: OverviewVessel;
  field: StatusField;
  columnId?: number;
  canWrite: boolean;
  onChange: (value: string) => void;
}) {
  const value = String(v[field.key] ?? '');
  const name = fieldName(field);
  const { data, isLoading, isError } = useCellHistory(columnId ? v.rowId : null, columnId ?? null);
  const nameOf = useNameResolver();
  const df = useDateFormatter();
  const last = data?.history?.[0];
  return (
    <li className="ovs-stat">
      <div className="ovs-stat-hd">
        <span className="ovs-stat-name">{name}</span>
        {field.title && field.title !== field.label && <span className="ovs-stat-abbr">{field.label}</span>}
        {!(canWrite && columnId) && <span className="ovs-stat-val"><StatusMark value={value} /></span>}
      </div>
      {canWrite && columnId ? (
        <div className={`ovs-seg ${statusKind(value)}`}>
          <Segmented value={value} onChange={onChange} ariaLabel={`${name} - ${v.name}`} options={STATUS_OPTIONS} />
        </div>
      ) : null}
      {columnId ? (
        <div className="ovs-hist">
          {isLoading ? <><LogoSpin size={14} decorative /> Loading history…</>
            : isError ? 'Couldn’t load the change history.'
            : data && !data.available ? 'History isn’t available on this Smartsheet plan.'
            : last ? `Last changed to ${df.maybe(last.value) || '–'} by ${last.by || nameOf(last.email) || 'Unknown'} · ${df.dateTime(last.at)}`
            : 'No recorded changes.'}
        </div>
      ) : null}
    </li>
  );
}

/** floating card showing a vessel's Assembly order status (opened from the clipboard button). */
function OrderStatusPopover({
  status,
  vessel,
  top,
  left,
}: {
  status?: AssemblyOrderStatus;
  vessel: string;
  top: number;
  left: number;
}) {
  if (!status) return null;
  return (
    <div data-ordpop role="dialog" aria-label={`Assembly order - ${vessel}`} className="ov-pop ov-pop--order" style={{ top, left }}>
      <div className="ov-pop-hd">Assembly order</div>
      <div className="ov-pop-sub">{vessel}</div>
      <OrderStatusBody status={status} />
    </div>
  );
}

/** printed / leader check / comments of a vessel's Assembly order: the desktop popover and the phone sheet */
function OrderStatusBody({ status }: { status: AssemblyOrderStatus }) {
  const nameOf = useNameResolver();
  const fmtDate = useFormatDate();
  return (
    <>
      <dl className="ov-ord">
        <dt>Printed</dt>
        <dd>{status.printedAt ? fmtDate(status.printedAt) : '–'}{status.printedBy ? ` · ${nameOf(status.printedBy)}` : ''}</dd>
        <dt>Leader check</dt>
        <dd>{status.leaderChecked ? `Yes${status.leaderBy ? ` · ${nameOf(status.leaderBy)}` : ''}${status.leaderAt ? ` · ${fmtDate(status.leaderAt)}` : ''}` : 'No'}</dd>
      </dl>
      {status.comments && (
        <div className="ov-ord-notes">
          <div className="ov-pop-sub">Comments</div>
          <div className="ov-ord-txt">{status.comments}</div>
        </div>
      )}
    </>
  );
}

/** floating, non-interactive card showing a cell's change history (newest first) on hover */
function CellHistoryPopover({
  rowId,
  columnId,
  label,
  vessel,
  top,
  left,
}: {
  rowId: number;
  columnId: number;
  label: string;
  vessel: string;
  top: number;
  left: number;
}) {
  const { data, isLoading } = useCellHistory(rowId, columnId);
  const nameOf = useNameResolver();
  const df = useDateFormatter();
  const entries = data?.history ?? [];
  return (
    <div className="ov-pop ov-pop--hist" style={{ top, left }}>
      <div className="ov-pop-hd">Cell history</div>
      <div className="ov-pop-sub">{vessel} · {label}</div>
      {isLoading && <InlineLoading className="ov-pop-note" />}
      {!isLoading && data && !data.available && <div className="ov-pop-note">History isn’t available on this Smartsheet plan.</div>}
      {!isLoading && data?.available && entries.length === 0 && <div className="ov-pop-note">No recorded changes.</div>}
      {entries.map((e, i) => (
        <div key={i} className="ov-hist-row">
          <Pill kind={pillKind(e.value)}>{statusKind(e.value) === 'hold' && <IconPause size={10} />}{df.maybe(e.value) || '–'}</Pill>
          <div className="ov-hist-who">
            <div className="ov-hist-name">{e.by || nameOf(e.email) || 'Unknown'}</div>
            <div className="ov-pop-note">{df.dateTime(e.at)}</div>
          </div>
        </div>
      ))}
    </div>
  );
}
